import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CloseConversationDialog, ConfirmDialog } from '@/components';
import { CapacityIndicator } from '@/components/doctor/CapacityIndicator.tsx';
import { ConversationList } from '@/components/doctor/ConversationList.tsx';
import { ConversationTabs, type ConversationTab } from '@/components/doctor/ConversationTabs.tsx';
import { SnippetsPanel } from '@/components/doctor/SnippetsPanel.tsx';
import { SystemActions } from '@/components/doctor/SystemActions.tsx';
import { avatarPalette, conversationDisplayName, conversationTimeLabel } from '@/components/conversationLabels.ts';
import { ChevronLeftIcon, CopyIcon, CopyCheckIcon, SparkleIcon, XIcon } from '@/components/icons.tsx';
import { getConfig } from '@/config.ts';
import { useAuthFetch } from '@/hooks';
import { getInitialsFromName, triggerDownload } from '@/utils';
import { ConversationThread, type ConversationThreadHandle } from '../ConversationThread.tsx';
import type { ConversationSummary, ExportConversationResponse, RequestPaymentKind, VerifyStatusResponse } from '@dermalink/shared';
import styles from './DoctorConsultations.module.css';

class ForbiddenError extends Error {}

function getTabForConversation(conv: ConversationSummary): ConversationTab {
  if (conv.status === 'closed') return 'closed';
  if (conv.status === 'pending_payment') return 'pending';
  if (conv.status === 'payment_requested') return 'active';
  if (conv.status === 'closure_requested') return 'active';
  if (conv.last_message_sender_cognito_sub === conv.patient_cognito_sub) return 'pending';
  return 'active';
}

/** True when the doctor may trigger the merged "Afronden" action (billing not yet decided, or a payment is still outstanding and can be re-picked). */
function canAfronden(conv: ConversationSummary | null): boolean {
  return !!conv && (
    conv.status === 'active'
    || conv.status === 'resolution_rejected'
    || conv.status === 'closure_requested'
    || conv.status === 'payment_requested'
  );
}

export function DoctorConsultations() {
  const authFetch = useAuthFetch();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Captured once at mount — must not track later URL writes from our own
  // selection-sync effect below, or every click would be misread as an
  // incoming deep link and reverted back to whatever the URL last held.
  const [deepLinkConversationId] = useState(() => searchParams.get('conversationId'));
  const appliedDeepLinkRef = useRef(false);
  const [selectedTab, setSelectedTab] = useState<ConversationTab>('pending');
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [showCloseDialog, setShowCloseDialog]           = useState(false);
  const [showForceCloseDialog, setShowForceCloseDialog] = useState(false);
  const [conversationCopied, setConversationCopied]     = useState(false);
  // Below 1400px the right pane is an off-canvas drawer instead of a column.
  const [toolsOpen, setToolsOpen]                       = useState(false);
  // Phones show the list and the open consult as separate screens.
  const [mobileView, setMobileView]                     = useState<'list' | 'consult'>(() => (deepLinkConversationId ? 'consult' : 'list'));
  const threadRef = useRef<ConversationThreadHandle>(null);

  async function handleCopyConversation() {
    const copied = await threadRef.current?.copyConversationToClipboard();
    if (!copied) return;
    setConversationCopied(true);
    setTimeout(() => setConversationCopied(false), 1500);
  }

  const { data: conversations = [], isPending, isFetching, isError, error } = useQuery({
    queryKey: ['doctor-conversations'],
    queryFn: async () => {
      const res = await authFetch('/doctor/conversations');
      if (res.status === 403) throw new ForbiddenError();
      if (!res.ok) throw new Error();
      return (await res.json()) as ConversationSummary[];
    },
    refetchInterval: 10_000,
    retry: (_, err) => !(err instanceof ForbiddenError),
  });

  const { data: verifyStatus } = useQuery<VerifyStatusResponse>({
    queryKey: ['doctor-verify-status'],
    queryFn: async () => {
      const res = await authFetch('/doctor/verify-status');
      if (!res.ok) throw new Error();
      return res.json();
    },
  });

  const isForbidden = isError && error instanceof ForbiddenError;
  const counts = useMemo(() => ({
    pending: conversations.filter((c) => getTabForConversation(c) === 'pending').length,
    active: conversations.filter((c) => getTabForConversation(c) === 'active').length,
    closed: conversations.filter((c) => getTabForConversation(c) === 'closed').length,
  }), [conversations]);

  // Mirrors the doctor's "occupied slot" definition in GET /doctors (routes/doctors.ts):
  // everything except closed and payment-related statuses counts against the doctor's cap.
  const occupiedSlots = useMemo(
    () => conversations.filter((c) => !['closed', 'pending_payment', 'payment_requested'].includes(c.status)).length,
    [conversations],
  );

  const filteredConversations = useMemo(() => {
    const inTab = conversations.filter((conv) => getTabForConversation(conv) === selectedTab);
    if (selectedTab !== 'closed') return inTab;
    // Not-yet-downloaded consults first, so they surface above the "Gedownload" divider.
    return [...inTab].sort((a, b) => Number(b.pdf_download_pending) - Number(a.pdf_download_pending));
  }, [conversations, selectedTab]);

  const closedDownloadSplitIndex = useMemo(() => {
    if (selectedTab !== 'closed') return undefined;
    const pendingCount = filteredConversations.filter((conv) => conv.pdf_download_pending).length;
    return pendingCount > 0 && pendingCount < filteredConversations.length ? pendingCount : undefined;
  }, [filteredConversations, selectedTab]);

  const selectedConversation = useMemo(
    () => conversations.find((conv) => conv.id === selectedConversationId) ?? null,
    [conversations, selectedConversationId],
  );

  useEffect(() => {
    let nextId = selectedConversationId;

    if (deepLinkConversationId && !appliedDeepLinkRef.current) {
      const target = conversations.find((conv) => String(conv.id) === deepLinkConversationId);
      if (target) {
        appliedDeepLinkRef.current = true;
        setSelectedTab(getTabForConversation(target));
        nextId = target.id;
      } else if (isFetching || conversations.length === 0) {
        // Cached conversations may not include the target yet (e.g. it was fetched
        // before this conversation existed) — wait for a settled fetch before giving up.
        return;
      } else {
        appliedDeepLinkRef.current = true;
      }
    }

    // Keep selection as long as the conversation exists anywhere — it may have moved tabs
    if (!(nextId && conversations.some((conv) => conv.id === nextId))) {
      nextId = filteredConversations[0]?.id ?? null;
    }

    if (nextId !== selectedConversationId) setSelectedConversationId(nextId);

    // Keep the URL in sync so the current conversation is bookmarkable/shareable and
    // survives a refresh — replace (not push) so browsing the inbox doesn't spam history.
    const desiredParam = nextId ? String(nextId) : null;
    if (searchParams.get('conversationId') !== desiredParam) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (desiredParam) next.set('conversationId', desiredParam); else next.delete('conversationId');
        return next;
      }, { replace: true });
    }
  }, [filteredConversations, conversations, deepLinkConversationId, isFetching, selectedConversationId, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedConversationId) return;
    queryClient.setQueryData<ConversationSummary[]>(['doctor-conversations'], (old) =>
      old?.map((conv) => conv.id === selectedConversationId ? { ...conv, unread_count: 0 } : conv) ?? []
    );
  }, [selectedConversationId, queryClient]);

  useEffect(() => {
    if (!toolsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setToolsOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toolsOpen]);

  function handleTabChange(tab: ConversationTab) {
    setSelectedTab(tab);
    // Explicitly pick the first conversation in the new tab on manual switch
    const first = conversations.find((conv) => getTabForConversation(conv) === tab);
    setSelectedConversationId(first?.id ?? null);
  }

  const closeMutation = useMutation({
    mutationFn: async (kind: RequestPaymentKind) => {
      if (!selectedConversationId) return;
      const res = await authFetch(`/conversations/${selectedConversationId}/request-payment`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ kind }),
      });
      if (!res.ok) throw new Error();
    },
    onSuccess: () => {
      setShowCloseDialog(false);
      queryClient.invalidateQueries({ queryKey: ['doctor-conversations'] });
      if (selectedConversationId) {
        queryClient.invalidateQueries({ queryKey: ['conversation', String(selectedConversationId)] });
      }
    },
  });

  const forceCloseMutation = useMutation({
    mutationFn: async () => {
      if (!selectedConversationId) return;
      const res = await authFetch(`/conversations/${selectedConversationId}/force-close`, { method: 'POST' });
      if (!res.ok) throw new Error();
    },
    onSuccess: () => {
      setShowForceCloseDialog(false);
      queryClient.invalidateQueries({ queryKey: ['doctor-conversations'] });
      if (selectedConversationId) {
        queryClient.invalidateQueries({ queryKey: ['conversation', String(selectedConversationId)] });
      }
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      if (!selectedConversationId) return;
      const res = await authFetch(`/conversations/${selectedConversationId}/export`, { method: 'POST' });
      if (!res.ok) throw new Error('Kon de PDF niet genereren. Probeer opnieuw.');
      const body = (await res.json()) as ExportConversationResponse;
      return body.url;
    },
    // Optimistic and permanent by design — this is a reminder aid, not a strict
    // download receipt, so it ticks on click and doesn't revert on failure.
    onMutate: () => {
      if (!selectedConversationId) return;
      queryClient.setQueryData<ConversationSummary[]>(['doctor-conversations'], (old) =>
        old?.map((c) => c.id === selectedConversationId ? { ...c, pdf_download_pending: false } : c) ?? []
      );
    },
    onSuccess: (url) => {
      if (!url) return;
      triggerDownload(url);
    },
  });

  function handleSelectConversation(id: number) {
    setSelectedConversationId(id);
    setMobileView('consult');
  }

  const selectedName = selectedConversation ? conversationDisplayName(selectedConversation) : '';
  const selectedPalette = avatarPalette(selectedName);

  const resourcesAppendDisabled =
    !selectedConversation
    || (selectedConversation.status !== 'active' && selectedConversation.status !== 'resolution_rejected');

  return (
    <div className={`${styles.consultations} ${mobileView === 'consult' ? styles.mobileConsult : styles.mobileList}`}>
      <section className={styles.listPane}>
        <CapacityIndicator
          className={styles.paneTitle}
          occupied={occupiedSlots}
          max={verifyStatus?.max_parallel_consultations}
        />
        <ConversationTabs selectedTab={selectedTab} counts={counts} onChange={handleTabChange} />
        {isPending    && <p className="text-muted status-message">Laden&hellip;</p>}
        {isForbidden  && <p className="text-muted status-message">U heeft geen toegang tot dit scherm. Enkel artsen kunnen patiëntengesprekken bekijken.</p>}
        {isError && !isForbidden && <p className="text-muted status-message">Kon gesprekken niet laden.</p>}
        {!isPending && !isError && filteredConversations.length === 0 && (
          <p className="text-muted status-message">Geen openstaande teleconsultaties.</p>
        )}
        {filteredConversations.length > 0 && (
          <div className={styles.list}>
            <ConversationList
              conversations={filteredConversations}
              selectedConversationId={selectedConversationId}
              onSelect={handleSelectConversation}
              splitIndex={closedDownloadSplitIndex}
              splitLabel="Consultatie Gedownload"
            />
          </div>
        )}
      </section>

      <section className={styles.workspacePane}>
        {selectedConversation && (
          <div className={styles.mobileHead}>
            <button type="button" className={styles.mobileBack} onClick={() => setMobileView('list')} aria-label="Terug naar lijst">
              <ChevronLeftIcon size={20} />
            </button>
            <span className={styles.mobileAvatar} style={{ background: selectedPalette.bg, color: selectedPalette.color }} aria-hidden="true">{getInitialsFromName(selectedName)}</span>
            <div className={styles.mobileHeadText}>
              <p className={styles.mobileHeadName}>{selectedName}</p>
              <p className={styles.mobileHeadSub}>{conversationTimeLabel(selectedConversation)}</p>
            </div>
            <button type="button" className={styles.mobileActions} onClick={() => setToolsOpen(true)} aria-expanded={toolsOpen} aria-controls="doctor-tools-pane">
              <SparkleIcon size={14} />
              Acties
            </button>
          </div>
        )}
        {selectedConversation && (
          <div className={styles.toolsBar}>
            <button type="button" className={styles.toolsToggle} onClick={() => setToolsOpen(true)} aria-expanded={toolsOpen} aria-controls="doctor-tools-pane">
              <SparkleIcon size={12} />
              Acties &amp; snippets
            </button>
          </div>
        )}
        {selectedConversation ? (
          <div className={styles.workspaceScroll}>
            <ConversationThread ref={threadRef} conversationId={String(selectedConversation.id)} embedded />
          </div>
        ) : (
          <p className="text-muted status-message">Selecteer links een consultatie om te starten.</p>
        )}
      </section>

      {toolsOpen && <div className={styles.toolsBackdrop} onClick={() => setToolsOpen(false)} aria-hidden="true" />}
      <aside id="doctor-tools-pane" className={`${styles.rightPane}${toolsOpen ? ` ${styles.rightPaneOpen}` : ''}`}>
        <div className={styles.drawerHeader}>
          <span className={styles.drawerTitle}>Acties &amp; snippets</span>
          <button type="button" className={styles.drawerClose} onClick={() => setToolsOpen(false)} aria-label="Sluiten">
            <XIcon size={16} />
          </button>
        </div>
        <SystemActions
          actions={[
            {
              id:          'close-consult',
              variant:     'green',
              label:       'Afronden',
              description: 'Patiënt ontvangt afsluitverzoek',
              onTrigger:   () => { setToolsOpen(false); setShowCloseDialog(true); },
              disabled:    !selectedConversation || closeMutation.isPending || !canAfronden(selectedConversation),
            },
            { id: 'force-close', variant: 'red', label: 'Sluiten zonder bevestiging', description: 'Consult wordt onmiddellijk gesloten', onTrigger: () => { setToolsOpen(false); setShowForceCloseDialog(true); }, disabled: !selectedConversation || forceCloseMutation.isPending || selectedConversation?.status === 'closed' },
            {
              id:          'copy-conversation',
              variant:     'blue',
              label:       'Conversatie naar plakbord',
              description: conversationCopied ? 'Gekopieerd naar plakbord' : 'Kopieer voor gebruik in medisch dossier',
              icon:        conversationCopied ? <CopyCheckIcon size={14} /> : <CopyIcon size={14} />,
              onTrigger:   handleCopyConversation,
              disabled:    !selectedConversation,
            },
            {
              id:          'download-pdf',
              variant:     'blue',
              label:       'Download als PDF',
              description: exportMutation.isPending
                ? 'PDF wordt gemaakt…'
                : exportMutation.isError
                  ? 'Kon de PDF niet genereren. Probeer opnieuw.'
                  : selectedConversation?.status === 'closed'
                    ? 'Exporteer voor eigen administratie'
                    : 'Enkel beschikbaar na afronden',
              onTrigger:   () => exportMutation.mutate(),
              disabled:    !selectedConversation || exportMutation.isPending || selectedConversation?.status !== 'closed',
            },
          ]}
        />
        <SnippetsPanel onAppend={(body) => { threadRef.current?.appendText(body); setToolsOpen(false); }} disabled={resourcesAppendDisabled} />
      </aside>
      {showCloseDialog && (
        <CloseConversationDialog
          paymentsEnabled={getConfig().payments_enabled}
          confirming={closeMutation.isPending}
          onConfirm={(kind) => closeMutation.mutate(kind)}
          onCancel={() => setShowCloseDialog(false)}
        />
      )}
      {showForceCloseDialog && (
        <ConfirmDialog
          title="Consult direct afsluiten?"
          message="Het consult wordt gesloten zonder bevestiging van de patiënt."
          confirmLabel="Afsluiten"
          variant="danger"
          confirming={forceCloseMutation.isPending}
          onConfirm={() => forceCloseMutation.mutate()}
          onCancel={() => setShowForceCloseDialog(false)}
        />
      )}
    </div>
  );
}
